import React, { useMemo, useState } from "react"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import type { TestCaseExpectedOutput } from "@/types/backend-contracts"
import { cn } from "@/utils/cn"

import { EVENTS, useEventDispatcher } from "../../../event-bus"
import type { EditableTestCase } from "../../../model"
import { InputCellWrapper } from "./InputCellWrapper"

interface CollapsedDataCellProps {
  testCase: EditableTestCase
  isExpanded: boolean
  width: string
  isMarkedForDeletion?: boolean
}

interface CollapsedBlob {
  metadata: TestCaseExpectedOutput
  expected_behaviors: string[]
}

function buildBlob(testCase: EditableTestCase): CollapsedBlob {
  return {
    metadata: testCase.expected_output_json ?? {},
    expected_behaviors: testCase.expected_behaviors ?? [],
  }
}

function formatBlob(blob: CollapsedBlob): string {
  return JSON.stringify(blob, null, 2)
}

interface ParseResult {
  ok: true
  updates: Partial<EditableTestCase>
}
interface ParseError {
  ok: false
  error: string
}

function parseBlob(raw: string): ParseResult | ParseError {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw) as unknown
  } catch {
    return { ok: false, error: "Invalid JSON" }
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, error: "Must be a JSON object" }
  }
  const obj = parsed as Record<string, unknown>

  const metadata = obj.metadata ?? {}
  if (
    metadata === null ||
    typeof metadata !== "object" ||
    Array.isArray(metadata)
  ) {
    return { ok: false, error: "`metadata` must be an object" }
  }

  const behaviors = obj.expected_behaviors ?? []
  if (
    !Array.isArray(behaviors) ||
    behaviors.some((b) => typeof b !== "string")
  ) {
    return {
      ok: false,
      error: "`expected_behaviors` must be an array of strings",
    }
  }

  return {
    ok: true,
    updates: {
      expected_output_json: metadata as TestCaseExpectedOutput,
      expected_behaviors: behaviors as string[],
    },
  }
}

/**
 * Single-cell collapsed view of a test case's structured data: metadata and
 * expected_behaviors rendered as one editable JSON blob. Edits commit both
 * fields at once on blur.
 */
export const CollapsedDataCell: React.FC<CollapsedDataCellProps> = ({
  testCase,
  isExpanded,
  width,
  isMarkedForDeletion = false,
}) => {
  const dispatch = useEventDispatcher()
  const display = useMemo(() => formatBlob(buildBlob(testCase)), [testCase])

  return (
    <InputCellWrapper
      width={width}
      isExpanded={isExpanded}
      isDisabled={isMarkedForDeletion}
      ariaLabel="Edit collapsed test case data"
    >
      {({ isEditing }) =>
        isEditing ? (
          <CollapsedDataEditor
            key={testCase.id}
            initial={display}
            onCommit={(updates) =>
              dispatch(EVENTS.TEST_CASE_UPDATE, {
                testCaseId: testCase.id,
                updates,
              })
            }
          />
        ) : (
          <pre
            className={cn(
              "text-xs font-mono text-coval-text whitespace-pre-wrap break-words m-0",
              isMarkedForDeletion ? "opacity-50 line-through" : undefined,
            )}
          >
            {display}
          </pre>
        )
      }
    </InputCellWrapper>
  )
}

interface EditorProps {
  initial: string
  onCommit: (updates: Partial<EditableTestCase>) => void
}

const CollapsedDataEditor: React.FC<EditorProps> = ({ initial, onCommit }) => {
  const [raw, setRaw] = useState(initial)
  const [error, setError] = useState<string | null>(null)

  const handleCommit = () => {
    if (raw === initial) {
      setError(null)
      return
    }
    const parsed = parseBlob(raw)
    if (!parsed.ok) {
      setError(parsed.error)
      return
    }
    setError(null)
    onCommit(parsed.updates)
  }

  return (
    <div className="flex flex-col gap-1">
      <JsonCodeEditor
        value={raw}
        onChange={setRaw}
        onBlur={handleCommit}
        rows={6}
        chromeless
        ariaLabel="Collapsed test case JSON"
        error={error}
      />
    </div>
  )
}
